import { useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
	eventCollectorClient,
	EventVerb,
	EventType,
} from 'services/eventCollectorService';

/**
 * Validates event parameters and returns mapped values or null if validation fails
 * Repo: event-collector-sdk/packages/js-event-collector/src/types.ts (EventType, EventVerb, etc)
 *
 * @param {string} product - Product name to validate
 * @param {string} eventType - Event type to validate and map
 * @param {string} verb - Event verb to validate and map
 * @returns {Object|null} Object with validEventType and validVerb, or null if validation fails
 */
const handleValidations = (product, eventType, verb) => {
	const EventTypeMap = {
		log: EventType.Log,
		metric: EventType.Metric,
		count: EventType.Count,
	};

	const EventVerbMap = {
		created: EventVerb.Created,
		updated: EventVerb.Updated,
		deleted: EventVerb.Deleted,
		exported: EventVerb.Exported,
		viewed: EventVerb.Viewed,
		started: EventVerb.Started,
		resumed: EventVerb.Resumed,
		imported: EventVerb.Imported,
		failed: EventVerb.Failed,
	};

	const ValidProducts = ['IEM'];

	// Validate product
	if (!ValidProducts.includes(product)) {
		console.error(
			`Invalid product: "${product}". Valid values are: ${ValidProducts.join(', ')}`
		);
		return null;
	}

	// Validate and map eventType
	const validEventType = EventTypeMap[eventType];
	if (!validEventType) {
		console.error(
			`Invalid eventType: "${eventType}". Valid values are: ${Object.keys(
				EventTypeMap
			).join(', ')}`
		);
		return null;
	}

	// Validate and map verb
	const validVerb = EventVerbMap[verb];
	if (!validVerb) {
		console.error(
			`Invalid verb: "${verb}". Valid values are: ${Object.keys(
				EventVerbMap
			).join(', ')}`
		);
		return null;
	}

	return { validEventType, validVerb };
};

/**
 * Sets up identity for event tracking using Salesforce IDs
 * Repo: event-collector-sdk/packages/js-event-collector/src/eventCollector.ts (identify method)
 *
 * @sample_event_payload
 * {
 *   "source_user_id": "user-123",  // from user.sf_user_id_in_client_org
 *   "sf_org_id": "00D000000000001"  // from tenant.salesforce_org_id
 * }
 *
 * @param {Object} user - User object from localStorage
 * @param {Object} tenant - Tenant object
 *
 */
const handleIdentity = (user, tenant) => {
	if (user?.sf_user_id_in_client_org || tenant?.salesforce_org_id) {
		eventCollectorClient.identify(
			user?.sf_user_id_in_client_org, // sets up event.source_user_id
			tenant?.salesforce_org_id // sets up event.sf_org_id (altvia user => null, tenant users => not null)
		);
	}
};

/**
 * Builds enriched metadata for event tracking
 * We're setting up the following metadata fields in the event payload:
 * @sample_event_payload
 * {
 *   "metadata": {
 *     product: 'IEM',
 *     target: 'work-queue-priority',
 *     session_id: '5c7865d8-65b5-4927-8b5c-66270e85006f',
 *     tenant_id: '123',
 *     tenant_short_name: 'alps',
 *     user_id: '456',
 *     email: 'user-at-example.com'
 *   }
 * }
 * @param {string} product - Product name
 * @param {string} target - Event target
 * @param {Object} tenant - Tenant object
 * @param {Object} user - User object from localStorage
 * @returns {Object} Enriched metadata object
 */
const handleMetadata = (product, target, tenant, user) => {
	const metadata = {
		product,
		target,
	};
	const sessionId = getSessionIdFromJWT();

	// Additional metadata fields if available
	if (sessionId) metadata.session_id = sessionId;
	if (tenant?.id) metadata.tenant_id = tenant.id;
	if (tenant?.short_name) metadata.tenant_short_name = tenant.short_name;
	if (user?.id) metadata.user_id = user.id;
	if (user?.email) metadata.email = user.email;

	// fallback to get the tenant id from the user
	if (!tenant && user?.tenant_id) metadata.tenant_id = user.tenant_id;

	return metadata;
};

/**
 * Extracts session ID from JWT token
 *
 * @returns {string|null} Session ID from JWT origin_jti claim, or null if not found
 */
const getSessionIdFromJWT = () => {
	const idToken = localStorage.getItem('idToken');
	if (!idToken) {
		return null;
	}

	try {
		const decoded = jwtDecode(idToken);
		return decoded.origin_jti;
	} catch (error) {
		console.error('Failed to decode JWT token:', error);
		return null;
	}
};

/**
 * Builds the event payload with optional parameters
 * We're setting up the following fields in the event payload:
 * @sample_event_payload
 * {
 *   "event_name": "BUTTON_CLICK",
 *   "event_type": "log",
 *   "verb": "created",
 *   "platform": "web",
 *   "source_domain_object": "task",											//optional
 *   "source_domain_object_id": "123",										//optional
 *   "before_data": {},																		//optional
 *   "changed_data": {},																	//optional
 *   "metadata": {
 *     ...
 *   }
 * }
 *
 * @param {string} eventName - Event name
 * @param {string} validVerb - Validated event verb
 * @param {string} validEventType - Validated event type
 * @param {Object} metadata - Metadata object
 * @param {Object} optionalEventParams - Optional event parameters
 * @returns {Object} Complete event payload
 */
const handleEventPayload = (
	eventName,
	validVerb,
	validEventType,
	metadata,
	optionalEventParams
) => {
	// Build event payload
	const eventPayload = {
		event_name: eventName,
		event_type: validEventType,
		verb: validVerb,
		platform: 'web',
		metadata: metadata,
	};

	// Extract optional event params
	const {
		source_domain_object,
		source_domain_object_id,
		before_data,
		changed_data,
	} = optionalEventParams;

	// Add optional fields to event payload
	// source_domain_object | source_domain_object_id | before_data | changed_data
	if (source_domain_object)
		eventPayload.source_domain_object = source_domain_object;
	if (source_domain_object_id)
		eventPayload.source_domain_object_id = source_domain_object_id;
	if (before_data) eventPayload.before_data = before_data;
	if (changed_data) eventPayload.changed_data = changed_data;

	return eventPayload;
};

/**
 * @resources
 * - Observability Tool: https://observe.app-x.com/browse/databases/2-observability-central (used to filter events)
 * - Event Collector SDK Repo: https://gitlab.com/altvia-product-engineering/observability/event-collector-sdk (sample event payloads)
 *
 * Custom hook for tracking events with standardized payload
 * @useTrackEvent hook parameters
 * @param {Object} options - Optional configuration for the hook
 * @param {Object} options.tenant - Tenant object containing id, short_name, salesforce_org_id
 * @param {string} options.product - Product name: "IEM" (defaults to "IEM")
 * @returns {Function} trackEvent - Function to track events
 *
 * @trackEvent function parameters
 * @param {string} product - Product name: "IEM"
 * @param {string} eventName - Event name: "BUTTON_CLICK"
 * @param {string} target - Event target: e.g., "work-queue-priority"
 * @param {string} eventType - Event type: "log", "metric", or "count"
 * @param {string} verb - Event verb: "created", "updated", "deleted", "exported", "viewed", "started", "resumed", "imported", or "failed"
 *
 * @sample_usage
 * const trackEvent = useTrackEvent({ tenant, product: 'IEM' });
 * trackEvent('IEM', 'BUTTON_CLICK', 'work-queue-priority', 'log', 'created', {});
 *
 * @sample_event_payload
 * eventCollectorClient.trackEvent(sampleEventPayload)
 * {
 *   "idempotency_key": "launchpad_ca061e53-9898-4f83-9309-ba220ed644b6_1787865289800",
 *   "source_service": "launchpad",
 *   "event_name": "BUTTON_CLICK",
 *   "event_type": "log",
 *   "verb": "created",
 *   "source_user_id": "005Hs00000I20VyIAJ", // user.sf_user_id_in_client_org
 *   "x_request_id": "ca061e53-9898-4f83-9309-ba220ed644b6",
 *   "platform": "web",
 *   "sf_org_id": "00DHs000002jl5i", // tenant.salesforce_org_id
 *   "metadata": {
 *     "product": "IEM",
 *     "target": "work-queue-priority",
 *     "session_id": "5c7865d8-65b5-4927-8b5c-66270e85006f",
 *     "tenant_id": "aimplayground",
 *     "tenant_short_name": "localaimplayground", //tenant.short_name
 *     "email": "sam+aimtenantadmin@altvia.com",
 *     "user_id": "samaimtenantadmin"
 *   }
 * }
 */
export const useTrackEvent = ({ tenant, product = 'IEM' } = {}) => {
	const trackEvent = useCallback(
		(product, eventName, target, eventType, verb, optionalEventParams = {}) => {
			// Validate parameters
			const isValidParams = handleValidations(product, eventType, verb);
			if (!isValidParams) {
				return;
			}
			const { validEventType, validVerb } = isValidParams;

			try {
				// Get user from localStorage
				const userStr = localStorage.getItem('user');
				const user = userStr ? JSON.parse(userStr) : null;

				handleIdentity(user, tenant);
				const metadata = handleMetadata(product, target, tenant, user);
				const eventPayload = handleEventPayload(
					eventName,
					validVerb,
					validEventType,
					metadata,
					optionalEventParams
				);

				eventCollectorClient.trackEvent(eventPayload);
			} catch (error) {
				console.error('Error tracking event:', error);
			}
		},
		[tenant]
	);

	const trackButtonClick = useCallback(
		(target, optionalEventParams = {}) => {
			trackEvent(
				product,
				'BUTTON_CLICK',
				target,
				'log',
				'created',
				optionalEventParams
			);
		},
		[trackEvent, product]
	);

	const trackPageLoad = useCallback(
		(target, optionalEventParams = {}) => {
			trackEvent(
				product,
				'PAGE_LOAD',
				target,
				'log',
				'viewed',
				optionalEventParams
			);
		},
		[trackEvent, product]
	);

	return { trackEvent, trackButtonClick, trackPageLoad };
};
